import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { Block } from "@blocknote/core";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { GripVertical, ChevronUp, ChevronDown, Trash2 } from "lucide-react";

// Content can be BlockNote blocks or markdown from AI
type NoteContent = Block[] | { markdown: string };

interface NoteBlockProps {
  initialContent?: NoteContent;
  isExpanded?: boolean;
  onChange?: (content: Block[]) => void;
  onToggle?: () => void;
  onDelete?: () => void;
  dragHandleProps?: Record<string, unknown>;
}

// Check if content is markdown format
function isMarkdownContent(content: NoteContent | undefined): content is { markdown: string } {
  return (
    content !== undefined &&
    content !== null &&
    typeof content === 'object' &&
    !Array.isArray(content) &&
    'markdown' in content
  );
}

export const NoteBlock = ({
  initialContent,
  isExpanded = true,
  onChange,
  onToggle,
  onDelete,
  dragHandleProps,
}: NoteBlockProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const lastLoadedContentRef = useRef<string | undefined>(undefined);

  // Determine initial blocks - use empty for markdown (will be parsed async)
  const getInitialBlocks = (): Block[] => {
    if (!initialContent) {
      return [{ type: "paragraph", content: [] } as Block];
    }
    if (isMarkdownContent(initialContent)) {
      // Will be parsed async, start with loading state
      return [{ type: "paragraph", content: [{ type: "text", text: "Loading...", styles: {} }] } as Block];
    }
    // Validate that initialContent is actually a non-empty array of blocks
    // BlockNote requires initialContent to be an array, not an object
    if (!Array.isArray(initialContent) || initialContent.length === 0) {
      console.warn('[NoteBlock] Invalid initialContent, expected array of blocks, got:', typeof initialContent);
      return [{ type: "paragraph", content: [] } as Block];
    }
    return initialContent;
  };

  const editor = useCreateBlockNote({
    initialContent: getInitialBlocks(),
  });

  // Parse markdown content when provided
  useEffect(() => {
    if (!editor) return;

    if (isMarkdownContent(initialContent)) {
      const markdown = initialContent.markdown;

      // Only parse if markdown changed
      if (markdown !== lastLoadedContentRef.current) {
        lastLoadedContentRef.current = markdown;

        editor.tryParseMarkdownToBlocks(markdown).then(blocks => {
          editor.replaceBlocks(editor.document, blocks);
          setIsInitialized(true);
        });
      }
    } else {
      setIsInitialized(true);
    }
  }, [editor, initialContent]);

  // Extract preview text from blocks or markdown
  const previewText = useMemo(() => {
    // If markdown content, extract first line
    if (isMarkdownContent(initialContent)) {
      const firstLine = initialContent.markdown.split('\n').find(l => l.trim() !== '');
      if (firstLine) {
        // Remove markdown syntax for preview
        return firstLine.replace(/^#+\s*/, '').replace(/\*\*/g, '').slice(0, 100) || "AI Response";
      }
      return "AI Response";
    }

    // Use initialContent only if it's a valid array, otherwise use editor.document
    const blocks = (Array.isArray(initialContent) && initialContent.length > 0)
      ? initialContent
      : editor.document;
    if (!blocks || blocks.length === 0) return "Empty note...";

    // Get text from first block
    const firstBlock = blocks[0];
    if (!firstBlock || !firstBlock.content || !Array.isArray(firstBlock.content)) {
      return "Empty note...";
    }

    // Extract text from inline content
    const text = firstBlock.content
      .map((item: { type?: string; text?: string }) => {
        if (item.type === 'text' && item.text) return item.text;
        return '';
      })
      .join('');

    return text || "Empty note...";
  }, [initialContent, editor.document]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <div
      className="group relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Drag handle - appears on hover, listeners on the handle itself */}
      <div
        className={`absolute -left-7 top-2 transition-opacity duration-150 ${isHovered ? 'opacity-100' : 'opacity-0'
          }`}
      >
        <div
          className="p-1 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400"
          {...dragHandleProps}
        >
          <GripVertical className="w-4 h-4" />
        </div>
      </div>

      {/* Delete button - appears on hover */}
      <div
        className={`absolute -right-7 top-2 transition-opacity duration-150 ${isHovered ? 'opacity-100' : 'opacity-0'
          }`}
      >
        <button
          onClick={onDelete}
          className="p-1 text-slate-300 hover:text-red-400 transition-colors"
          title="Delete note"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Note content with subtle left border */}
      <div className={`relative border-l-2 border-slate-200/60 pl-4 transition-all duration-200 ${isHovered ? 'border-slate-300' : ''
        }`}>
        {/* Click the left border to toggle expand/collapse */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggle?.();
          }}
          className="absolute left-0 top-0 bottom-0 w-3 bg-transparent cursor-pointer"
          aria-label={isExpanded ? "Collapse note" : "Expand note"}
          title={isExpanded ? "Collapse" : "Expand"}
        />
        {isExpanded ? (
          /* Expanded: Full editor - hide BlockNote's side menu to avoid conflict */
          <div className="[&_.bn-container]:!pl-0 [&_.bn-editor]:!pl-0 [&_.bn-block-group]:!pl-0 [&_.bn-side-menu]:hidden [&_code]:!text-slate-800 [&_code]:!bg-slate-100 [&_.bn-inline-content_code]:!text-pink-600 [&_.bn-inline-content_code]:!bg-pink-50 [&_pre]:!bg-slate-100 [&_pre]:!text-slate-800">
            <BlockNoteView
              editor={editor}
              theme="light"
              sideMenu={false}
              onChange={() => {
                onChange?.(editor.document);
              }}
            />
          </div>
        ) : (
          /* Collapsed: Preview text */
          <div className="w-full text-left py-2">
            <span className="text-sm text-slate-500 truncate block">
              {previewText}
            </span>
          </div>
        )}

        {/* Collapse affordance when expanded - chevron points up */}
        {isExpanded && (
          <button
            onClick={onToggle}
            className="mt-1 mb-2 text-xs text-slate-300 hover:text-slate-400 transition-colors flex items-center gap-1"
          >
            <ChevronUp className="w-3 h-3" />
            <span>Collapse</span>
          </button>
        )}

        {/* Expand affordance when collapsed - matches collapse styling */}
        {!isExpanded && (
          <button
            onClick={onToggle}
            className="mt-1 mb-2 text-xs text-slate-300 hover:text-slate-400 transition-colors flex items-center gap-1"
          >
            <ChevronDown className="w-3 h-3" />
            <span>Expand</span>
          </button>
        )}
      </div>
    </div>
  );
};
